import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, HelpCircle } from 'lucide-react';
import { CurrencySelector } from './currency-selector.js';

// Loose t signature — useTranslation's full TFunction type pulls in too
// much generic noise for what's effectively a simple string interpolator
// in chrome components.
type TFunction = (key: string, options?: Record<string, unknown>) => string;

// Persistent top bar. Three-column grid so the search trigger pins to
// the viewport centre regardless of how much chrome sits on either
// side. Left column = brand; centre = search; right column = chrome
// icons, market filter, user region — in that order so the market
// drop-down sits adjacent to the user pane on the far right.
//
// Workspace tabs (Landing / Alex / Help / + …) used to live inline here
// between the brand and the search; they now sit on their own row below
// the header, centered, via <TopBarTabsRow> in App.tsx. The command
// palette opens via ⌘K, clicking the search trigger, or the "+" in the
// tab strip — the dedicated Add Module button was redundant.

export interface TopBarProps {
  onOpenPalette: () => void;
  userDisplayName: string;
  userEmail?: string;
  userFirm?: string;
  onSignOut?: () => void;
  /** Help icon — usually switches to the Help tab. */
  onOpenHelp?: () => void;
  /** Notifications (alerts) bell — opens the Alerts panel. */
  onOpenNotifications?: () => void;
  /** Unread alert count for the bell badge (omit/0 → no badge). */
  notificationCount?: number;
}

export function TopBar(props: TopBarProps) {
  const { t } = useTranslation();
  const mac = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /mac/i.test(navigator.platform);
  }, []);
  const shortcutKey = mac ? '⌘ K' : 'Ctrl K';

  return (
    <header
      style={{
        height: 'var(--topbar-h)',
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 'var(--s-3)',
        padding: '0 var(--s-3)',
        // Chrome layer — the lighter cream surface. The canvas below
        // (the area between panels) is the darker layer, so chrome
        // visually "floats above" rather than "presses down".
        background: 'var(--surface)',
        borderBottom: '1px solid var(--hairline)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', minWidth: 0 }}>
        <BrandMark />
      </div>
      <SearchTrigger onOpen={props.onOpenPalette} shortcutKey={shortcutKey} t={t} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          justifyContent: 'flex-end',
          minWidth: 0,
        }}
      >
        <CurrencySelector />
        <ChromeIconCluster
          onOpenHelp={props.onOpenHelp}
          onOpenNotifications={props.onOpenNotifications}
          notificationCount={props.notificationCount}
          t={t}
        />
        <UserRegion
          name={props.userDisplayName}
          email={props.userEmail}
          firm={props.userFirm}
          onSignOut={props.onSignOut}
          t={t}
        />
      </div>
    </header>
  );
}

// --- 1. Brand mark ----------------------------------------------------

function BrandMark() {
  return (
    <button
      type="button"
      aria-label="Corryk home"
      onClick={() => {
        // Routing isn't wired yet — this is the home affordance. Once we
        // have a default workspace concept it'll restore that layout.
        // For now it's a no-op click target.
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--s-1)',
        background: 'transparent',
        border: 'none',
        padding: '0 var(--s-1)',
        cursor: 'pointer',
        color: 'var(--text-primary)',
        // Brand wordmark — Fraunces serif, deliberately distinct from the
        // platform's general --font-display (which stays mapped to the
        // Inter UI face used by panel titles + section headers).
        fontFamily: '"Fraunces", "Source Serif 4", Georgia, serif',
        fontSize: '24px',
        fontWeight: 500,
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}
    >
      Corryk
    </button>
  );
}

// --- 2. Search trigger ------------------------------------------------

function SearchTrigger({
  onOpen,
  shortcutKey,
  t,
}: {
  onOpen: () => void;
  shortcutKey: string;
  t: TFunction;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-haspopup="dialog"
      aria-label={t('topBar.searchAria')}
      style={{
        // Width is fluid up to a sensible cap so the bar feels centred
        // on wide displays and still shrinks gracefully when chrome on
        // the right starts to claw back space. Min-width keeps the
        // shortcut chip readable when the right column is heavy.
        width: 'min(480px, 40vw)',
        minWidth: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--s-2)',
        padding: '3px var(--s-2)',
        height: 22,
        // White search-bar fill — pops as an "input field" against the
        // cream chrome, matches every other search bar in the app.
        background: 'var(--menu-surface)',
        border: '1px solid var(--hairline)',
        borderRadius: 2,
        color: 'var(--text-tertiary)',
        fontSize: 'var(--t-body)',
        cursor: 'pointer',
        transition: 'border-color 80ms',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.borderColor = 'var(--text-tertiary)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.borderColor = 'var(--hairline)';
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {t('topBar.searchPlaceholder')}
      </span>
      <span
        className="kbd"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--t-meta)',
          padding: '0 var(--s-1)',
          border: '1px solid var(--hairline)',
          borderRadius: 2,
          color: 'var(--text-tertiary)',
          background: 'var(--bg)',
          flexShrink: 0,
        }}
      >
        {shortcutKey}
      </span>
    </button>
  );
}

// --- 4.5. Chrome icons (mail + help) --------------------------------

function ChromeIconCluster({
  onOpenHelp,
  onOpenNotifications,
  notificationCount = 0,
  t,
}: {
  onOpenHelp?: () => void;
  onOpenNotifications?: () => void;
  notificationCount?: number;
  t: TFunction;
}) {
  const hasUnread = notificationCount > 0;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--s-1)' }}>
      {onOpenNotifications && (
        <IconBtn
          onClick={onOpenNotifications}
          ariaLabel={t('topBar.notificationsAria')}
          title={
            hasUnread
              ? `${notificationCount} new alert${notificationCount === 1 ? '' : 's'}`
              : t('topBar.notifications')
          }
        >
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Bell size={14} />
            {hasUnread && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -6,
                  minWidth: 14,
                  height: 14,
                  padding: '0 3px',
                  boxSizing: 'border-box',
                  borderRadius: 7,
                  background: 'var(--accent)',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: '14px',
                  textAlign: 'center',
                }}
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </span>
        </IconBtn>
      )}
      {onOpenHelp && (
        <IconBtn
          onClick={onOpenHelp}
          ariaLabel={t('topBar.helpAria')}
          title={t('topBar.help')}
          filled
        >
          <HelpCircle size={14} />
        </IconBtn>
      )}
    </div>
  );
}

function IconBtn({
  onClick,
  ariaLabel,
  title,
  children,
  filled,
}: {
  onClick: () => void;
  ariaLabel: string;
  title: string;
  children: React.ReactNode;
  /** filled = a visible button on the paper --bg with a hairline border
   *  (vs the default transparent chrome icon). */
  filled?: boolean;
}) {
  const base = filled ? 'var(--bg)' : 'transparent';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 22,
        padding: 0,
        border: `1px solid ${filled ? 'var(--hairline)' : 'transparent'}`,
        borderRadius: 2,
        background: base,
        color: filled ? 'var(--text-secondary)' : 'var(--text-tertiary)',
        cursor: 'pointer',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = 'var(--hover-subtle)';
        e.currentTarget.style.color = 'var(--text-primary)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = base;
        e.currentTarget.style.color = filled ? 'var(--text-secondary)' : 'var(--text-tertiary)';
      }}
    >
      {children}
    </button>
  );
}

// --- 5. User region --------------------------------------------------

function UserRegion({
  name,
  email,
  firm,
  onSignOut,
  t,
}: {
  name: string;
  email?: string;
  firm?: string;
  onSignOut?: () => void;
  t: TFunction;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        flexShrink: 0,
      }}
    >
      <span
        title={email}
        style={{
          fontSize: 'var(--t-body)',
          color: 'var(--text-primary)',
        }}
      >
        {name}
        {firm && (
          <>
            <span style={{ color: 'var(--text-tertiary)', margin: '0 var(--s-1)' }}>·</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{firm}</span>
          </>
        )}
      </span>
      {onSignOut && (
        <button
          type="button"
          onClick={onSignOut}
          style={{
            fontSize: 'var(--t-meta)',
            padding: '1px var(--s-1)',
            border: '1px solid var(--hairline)',
            borderRadius: 2,
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          {t('topBar.signOut')}
        </button>
      )}
    </div>
  );
}
